/* oxlint-disable jsx-a11y/no-static-element-interactions */
import { useMemo, useState } from "react";

import { PlusIcon } from "@/assets/icons/general/Plus";
import { XIcon } from "@/assets/icons/ui/X";
import { Button } from "@/components/ui/buttons/Button/Button";
import { Dropdown } from "@/components/ui/inputs/Dropdown/Dropdown";
import { Typography } from "@/components/ui/text/Typography/Typography";
import { BizProfileQueries } from "@/data/bizProfile/bizProfile.queries";
import { BizTagsQueries } from "@/data/bizTags/bizTags.queries";

interface UploadsPagePillFiltersProps {
  selectedTags: Map<string, string[]>;
  setSelectedTags: (tags: Map<string, string[]>) => void;
}

export const experienceFilter = [
  { id: "0", name: "< 1y", quantileId: "a_pct10" },
  { id: "1", name: "1 - 5y", quantileId: "a_pct25" },
  { id: "2", name: "5 - 10y", quantileId: "a_median" },
  { id: "3", name: "10 - 15y", quantileId: "a_pct75" },
  { id: "4", name: "> 15y", quantileId: "a_pct75" },
];

interface PillFilterDropdownProps {
  dropdownData: { id: string; name: string }[];
  selectedTagsData: string[];
  setSelectedTags: (newData: string[]) => void;
  placeholder: string;
  label: string;
  searchable?: boolean;
  isSingleSelect?: boolean;
  forceUnselectedAppearance?: boolean;
}

const PillFilterDropdown = ({
  dropdownData,
  selectedTagsData,
  setSelectedTags,
  placeholder,
  label,
  searchable,
  isSingleSelect,
  forceUnselectedAppearance,
}: PillFilterDropdownProps) => {
  const [search, setSearch] = useState("");

  const selectedItems = useMemo(() => {
    return dropdownData.filter((item) =>
      selectedTagsData?.some((selected) => selected.toString() === item.id.toString()),
    );
  }, [dropdownData, selectedTagsData]);

  // Create stable item references that won't change
  const items = useMemo(
    () =>
      dropdownData.map((data) => ({
        label: data.name,
        id: data.id.toString(),
      })),
    [dropdownData],
  );

  // Filter items based on search
  const filteredItems = useMemo(() => {
    if (!search) return items;
    const searchLower = search.toLowerCase();
    return items.filter((item) => item.label.toLowerCase().includes(searchLower));
  }, [items, search]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
  };

  const handleClearFilter = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setSelectedTags([]);
  };

  // Display as pill when items are selected (unless forced to show unselected appearance)
  if (selectedItems.length > 0 && !forceUnselectedAppearance) {
    const displayText = selectedItems.map((item) => item.name).join(", ");

    if (isSingleSelect) {
      return (
        <Dropdown
          label=""
          hideLabels
          items={filteredItems}
          value={selectedTagsData?.[0] || ""}
          onChange={(newDropdownSelection: string) => {
            if (selectedTagsData.includes(newDropdownSelection)) {
              setSelectedTags([]);
            } else {
              setSelectedTags([newDropdownSelection]);
            }
          }}
          selectionMode="single"
          variant="outlined"
          placeholder={placeholder}
          showSearch={searchable}
          search={search}
          onSearchChange={handleSearchChange}
          customTrigger={
            <div className="flex h-7 items-center gap-2 rounded-4xl border border-elevation-outline-1 bg-elevation-surface-2 px-3 py-height-xs transition-colors">
              <Button
                variant="outlined"
                className="!gap-0 !p-0 border-none bg-transparent hover:bg-transparent"
                width="auto"
              >
                <Typography
                  size="label-4"
                  variant="prominent-2"
                  className="whitespace-nowrap text-text-default-primary"
                >
                  {selectedItems.length === 1 ? displayText : `${label} (${selectedItems.length})`}
                </Typography>
              </Button>
              <div
                onClick={handleClearFilter}
                className="flex h-4 w-4 items-center justify-center rounded-full transition-colors"
              >
                <XIcon className="!text-text-inverted-tertiary h-3 w-3" />
              </div>
            </div>
          }
        />
      );
    }

    return (
      <Dropdown
        label=""
        hideLabels
        items={filteredItems}
        value={selectedTagsData || []}
        onChange={(newDropdownSelection: string[]) => {
          setSelectedTags(newDropdownSelection);
        }}
        selectionMode="multiple"
        variant="outlined"
        placeholder={placeholder}
        showSearch={searchable}
        search={search}
        onSearchChange={handleSearchChange}
        customTrigger={
          <div className="flex items-center gap-2 rounded-4xl border border-elevation-outline-1 bg-elevation-surface-2 px-3 py-height-xs transition-colors">
            <Button
              variant="outlined"
              className="!gap-0 !p-0 border-none bg-transparent hover:bg-transparent"
              width="auto"
            >
              <Typography
                size="label-4"
                variant="prominent-2"
                className="whitespace-nowrap text-text-default-primary"
              >
                {selectedItems.length === 1 ? displayText : `${label} (${selectedItems.length})`}
              </Typography>
            </Button>
            <div
              onClick={handleClearFilter}
              className="flex h-4 w-4 items-center justify-center rounded-full transition-colors"
            >
              <XIcon className="h-3 w-3 text-text-inverted-tertiary" />
            </div>
          </div>
        }
      />
    );
  }

  // Display as regular button when no items are selected
  if (isSingleSelect) {
    return (
      <Dropdown
        label=""
        hideLabels
        items={filteredItems}
        value={selectedTagsData?.[0] || ""}
        onChange={(newDropdownSelection: string) => {
          if (selectedTagsData.includes(newDropdownSelection)) {
            setSelectedTags([]);
          } else {
            setSelectedTags([newDropdownSelection]);
          }
        }}
        selectionMode="single"
        variant="outlined"
        placeholder={placeholder}
        showSearch={searchable}
        search={search}
        onSearchChange={handleSearchChange}
        customTrigger={
          <Button
            icon={PlusIcon}
            iconClassName="w-2 h-2"
            variant="outlined"
            className="!gap-2 h-7 rounded-full border border-elevation-outline-1 px-3 py-height-xs transition-colors"
            width="auto"
          >
            <Typography
              size="label-4"
              variant="prominent-2"
              className="text-text-default-secondary"
            >
              {label}
            </Typography>
          </Button>
        }
      />
    );
  }

  return (
    <Dropdown
      label=""
      hideLabels
      items={filteredItems}
      value={selectedTagsData || []}
      onChange={(newDropdownSelection: string[]) => {
        setSelectedTags(newDropdownSelection);
      }}
      selectionMode="multiple"
      variant="outlined"
      placeholder={placeholder}
      showSearch={searchable}
      search={search}
      onSearchChange={handleSearchChange}
      customTrigger={
        <Button
          icon={PlusIcon}
          iconClassName="w-2 h-2"
          variant="outlined"
          className="!gap-2 h-7 rounded-full border border-elevation-outline-1 px-3 py-height-xs transition-colors"
          width="auto"
        >
          <Typography
            size="label-4"
            variant="prominent-2"
            className="text-text-default-secondary"
          >
            {label}
          </Typography>
        </Button>
      }
    />
  );
};

export const UploadsPagePillFilters = ({ selectedTags, setSelectedTags }: UploadsPagePillFiltersProps) => {
  const { data: tags } = BizTagsQueries.useListTags({ limit: 50 });
  const { data: jobTitles } = BizProfileQueries.useListJobTitles({ limit: 50 });
  const { data: locations } = BizProfileQueries.useListLocations({ limit: 50 });
  const ratings = useMemo(
    () => [
      { id: "-1", name: "Unrated" },
      { id: "1", name: "1" },
      { id: "2", name: "2" },
      { id: "3", name: "3" },
      { id: "4", name: "4" },
      { id: "5", name: "5" },
    ],
    [],
  );

  const allFilters = useMemo(
    () => ({
      jobTitle:
        jobTitles?.items?.map((item) => ({
          id: item.jobTitle,
          name: item.jobTitle,
        })) ?? [],
      tag: [
        { id: "", name: "No Tags" }, // Special item for profiles without tags
        ...(tags?.items?.map((tag) => ({
          id: tag.name,
          name: tag.name,
        })) ?? []),
      ],
      location:
        locations?.items?.map((location) => ({
          id: location.location ?? "",
          name: location.location ?? "",
        })) ?? [],
      rating: ratings,
      experience: experienceFilter,
    }),
    [jobTitles, tags, locations, ratings],
  );

  return (
    <div className="flex flex-row flex-wrap gap-2">
      <PillFilterDropdown
        dropdownData={allFilters.jobTitle}
        isSingleSelect
        selectedTagsData={selectedTags.get("jobTitle") || []}
        setSelectedTags={(newSelectedJobTitle) => {
          setSelectedTags(new Map(selectedTags).set("jobTitle", newSelectedJobTitle));
        }}
        placeholder="Select Job Title"
        label="Job Title"
        searchable
      />
      <PillFilterDropdown
        isSingleSelect
        dropdownData={allFilters.location}
        selectedTagsData={selectedTags.get("location") || []}
        setSelectedTags={(newSelectedLocation) => {
          setSelectedTags(new Map(selectedTags).set("location", newSelectedLocation));
        }}
        placeholder="Select Location"
        label="Location"
        searchable
      />
      <PillFilterDropdown
        dropdownData={allFilters.experience}
        selectedTagsData={selectedTags.get("experience") || []}
        setSelectedTags={(newSelectedExperience) => {
          setSelectedTags(new Map(selectedTags).set("experience", newSelectedExperience));
        }}
        placeholder="Select Experience"
        label="Exp"
        searchable={false}
        isSingleSelect
      />

      {/* Individual pill buttons for each selected tag */}
      {(selectedTags.get("tag") || []).map((tagId) => {
        // Handle the special "No Tags" case (empty string)
        const tagData = tagId === "" ? { id: "", name: "No Tags" } : allFilters.tag.find((tag) => tag.id === tagId);

        if (!tagData) return null;

        return (
          <div
            key={tagId === "" ? "no-tags" : tagId}
            className="flex items-center gap-2 rounded-4xl border border-elevation-outline-1 bg-elevation-surface-2 px-3 py-height-xs transition-colors"
          >
            <Typography
              size="label-4"
              variant="prominent-2"
              className="whitespace-nowrap text-text-default-primary"
            >
              {tagData.name}
            </Typography>
            <div
              onClick={() => {
                const currentTags = selectedTags.get("tag") || [];
                const newTags = currentTags.filter((id) => id !== tagId);
                setSelectedTags(new Map(selectedTags).set("tag", newTags));
              }}
              className="flex h-4 w-4 cursor-pointer items-center justify-center rounded-full transition-colors"
            >
              <XIcon className="h-3 w-3 text-text-inverted-tertiary" />
            </div>
          </div>
        );
      })}

      {/* Dropdown that always looks unselected but shows checked options */}
      <PillFilterDropdown
        dropdownData={allFilters.tag}
        selectedTagsData={selectedTags.get("tag") || []}
        setSelectedTags={(newSelectedTag) => {
          setSelectedTags(new Map(selectedTags).set("tag", newSelectedTag));
        }}
        placeholder="Select Tag"
        label="Tag"
        searchable
        forceUnselectedAppearance
      />

      <PillFilterDropdown
        dropdownData={allFilters.rating}
        selectedTagsData={selectedTags.get("rating") || []}
        setSelectedTags={(newSelectedRating) => {
          setSelectedTags(new Map(selectedTags).set("rating", newSelectedRating));
        }}
        placeholder="Select Rating"
        label="Rating"
        searchable={false}
        isSingleSelect
      />
    </div>
  );
};
